import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, Sparkles, Repeat, Trash2, X, Check, Zap } from 'lucide-react';
import { api } from '../../lib/api';
import { Platform, PLATFORM_NAMES } from '@crosova/shared';
import { useExtensionDetected, triggerExtensionCrosslist, triggerExtensionDelist, isExtensionPlatform } from '../../lib/extension';

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'live', label: 'Live' },
  { value: 'draft', label: 'Draft' },
  { value: 'sold', label: 'Sold' },
];

const PLATFORM_COLOURS: Record<string, string> = {
  ebay: '#e53935', vinted: '#09b864', depop: '#ff2300', etsy: '#f56040', facebook: '#1877f2',
};

export function InventoryPage() {
  const [filter, setFilter] = useState<string>('all');
  const [platformFilter, setPlatformFilter] = useState<string>('');
  const [showPlatformFilter, setShowPlatformFilter] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [crosslistTarget, setCrosslistTarget] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState(0);
  const [deleteTotal, setDeleteTotal] = useState(0);
  const [limit, setLimit] = useState(100);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') ?? '';

  // Reset limit when filters change
  const resetLimit = () => setLimit(100);

  const { data, isLoading } = useQuery({
    queryKey: ['listings', filter, platformFilter, searchQuery, limit],
    queryFn: () => api.get('/listings', {
      params: {
        status: filter === 'all' ? undefined : filter,
        platform: platformFilter || undefined,
        search: searchQuery || undefined,
        limit,
      },
    }).then((r) => r.data),
  });

  const { data: platforms = [] } = useQuery({
    queryKey: ['platforms'],
    queryFn: () => api.get('/platforms').then((r) => r.data),
  });
  const connectedPlatforms: string[] = platforms.filter((p: any) => p.connected).map((p: any) => p.platform);

  const items: any[] = data?.items ?? [];
  const stats = data?.stats ?? { active: 0, revenue: 0, sold: 0, failed: 0 };

  // ── Single-item mutations ────────────────────────────────────────────────
  const singleDelete = useMutation({
    mutationFn: (id: string) => api.delete(`/listings/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['listings'] }); setDeleteTarget(null); },
  });

  const extensionDetected = useExtensionDetected();

  const singleCrosslist = useMutation({
    mutationFn: async ({ listingId, platforms }: { listingId: string; platforms: string[] }) => {
      const result = await api.post(`/listings/${listingId}/crosslist`, { platforms }).then((r) => r.data);

      // Trigger extension for Vinted/Depop platforms
      const extensionPlatforms = platforms.filter(isExtensionPlatform);
      if (extensionPlatforms.length > 0) {
        const { data: listing } = await api.get(`/listings/${listingId}`);
        for (const platform of extensionPlatforms) {
          triggerExtensionCrosslist(
            result.jobId,
            {
              title: listing.title,
              description: listing.description,
              price: listing.price,
              condition: listing.condition,
              brand: listing.brand,
              size: listing.size,
              colour: listing.colour,
              tags: listing.tags,
              photos: listing.photos?.map((p: any) => p.url) ?? [],
            },
            platform,
          );
        }
      }

      return result;
    },
    onSuccess: () => { setCrosslistTarget(null); navigate('/dashboard/jobs'); },
  });

  const removeFromPlatform = useMutation({
    mutationFn: ({ listingId, platform }: { listingId: string; platform: string }) =>
      api.post(`/listings/${listingId}/platforms/${platform}/remove`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['listings'] }); },
  });

  // Real delist: opens the platform item page via the extension and deletes it.
  const delistViaExtension = useMutation({
    mutationFn: async ({ listingId, platform }: { listingId: string; platform: string }) => {
      const { data } = await api.post<{ jobId: string; itemUrl: string; externalId: string | null }>(
        `/extension/delist`, { listingId, platform },
      );
      return { ...data, platform };
    },
    onSuccess: (data) => {
      triggerExtensionDelist(data.jobId, data.platform, data.itemUrl, data.externalId);
      setCrosslistTarget(null);
      // The extension deletes on the platform and reports back; refresh shortly after.
      setTimeout(() => qc.invalidateQueries({ queryKey: ['listings'] }), 6000);
    },
    onError: (err: any) => {
      if (err?.response?.status === 422) {
        alert('Crosova has no saved link for this Vinted listing, so it can\'t open it to delete. If you\'ve already removed it on Vinted, use "Not listed?" instead.');
      } else {
        alert('Could not start the delist. Please try again.');
      }
    },
  });

  // ── Bulk mutations ───────────────────────────────────────────────────────
  const bulkDelete = useMutation({
    mutationFn: async () => {
      const ids = Array.from(selected);
      setDeleteTotal(ids.length);
      setDeleteProgress(0);
      for (const id of ids) {
        await api.delete(`/listings/${id}`);
        setDeleteProgress((p) => p + 1);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['listings'] });
      setSelected(new Set());
      setBulkDeleteConfirm(false);
      setDeleteProgress(0);
      setDeleteTotal(0);
    },
    onError: () => {
      qc.invalidateQueries({ queryKey: ['listings'] });
      setBulkDeleteConfirm(false);
      setDeleteProgress(0);
      setDeleteTotal(0);
    },
  });

  function toggleSelect(id: string) {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  }

  const crosslistTargetItem = crosslistTarget ? items.find((i) => i.id === crosslistTarget) : null;
  const targetLivePlatforms: string[] = crosslistTargetItem
    ? (crosslistTargetItem.platformListings ?? []).filter((pl: any) => pl.status === 'live').map((pl: any) => pl.platform as string)
    : [];

  return (
    <div className="flex flex-col gap-3.5">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-base font-semibold tracking-tight">Inventory</h1>
          <p className="text-[11.5px] text-white/25 mt-0.5">
            {items.length} items{connectedPlatforms.length > 0 ? ` · ${connectedPlatforms.length} platform${connectedPlatforms.length !== 1 ? 's' : ''} connected` : ''}
            {searchQuery && <span className="text-primary-300"> · searching "{searchQuery}"</span>}
          </p>
        </div>
        <div className="flex gap-1.5 items-center">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => { setFilter(f.value); resetLimit(); }}
              className={`px-2.5 py-1 rounded-md text-[11px] border transition-colors ${
                filter === f.value
                  ? 'border-primary-500/50 text-primary-300 bg-primary-500/10'
                  : 'border-white/[0.08] text-white/35 bg-white/[0.03]'
              }`}
            >
              {f.label}
            </button>
          ))}
          <div className="relative">
            <button
              onClick={() => setShowPlatformFilter(!showPlatformFilter)}
              className={`px-2.5 py-1 rounded-md text-[11px] border flex items-center gap-1 transition-colors ${
                platformFilter ? 'border-primary-500/50 text-primary-300 bg-primary-500/10' : 'border-white/[0.08] text-white/35 bg-white/[0.03]'
              }`}
            >
              <SlidersHorizontal size={11} />
              {platformFilter ? PLATFORM_NAMES[platformFilter as Platform] ?? platformFilter : 'Filter'}
            </button>
            {showPlatformFilter && (
              <div className="absolute right-0 top-8 bg-bg-raised border border-white/[0.1] rounded-xl w-40 shadow-xl z-20 overflow-hidden">
                <button onClick={() => { setPlatformFilter(''); setShowPlatformFilter(false); }} className={`w-full text-left px-3 py-2 text-[12px] hover:bg-white/[0.04] ${!platformFilter ? 'text-primary-300' : 'text-white/60'}`}>All platforms</button>
                {connectedPlatforms.map((p) => (
                  <button key={p} onClick={() => { setPlatformFilter(p); setShowPlatformFilter(false); }} className={`w-full text-left px-3 py-2 text-[12px] hover:bg-white/[0.04] capitalize ${platformFilter === p ? 'text-primary-300' : 'text-white/60'}`}>
                    {PLATFORM_NAMES[p as Platform] ?? p}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        <StatCard label="Active listings" value={stats.active ?? 0} change={stats.active > 0 ? `${items.filter((i: any) => i.status === 'live').length} live` : 'No listings yet'} colour="primary" />
        <StatCard label="Revenue (30d)" value={`£${((stats.revenue ?? 0) / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} change={stats.revenue > 0 ? 'This month' : 'No sales yet'} colour="secondary" />
        <StatCard label="Items sold" value={stats.sold ?? 0} change={stats.sold > 0 ? 'Total sold' : 'No sales yet'} colour="success" />
        <StatCard label="Failed listings" value={stats.failed ?? 0} change={stats.failed > 0 ? 'Needs attention' : 'All good'} colour="danger" />
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
        <div className="min-w-[560px]">
        {/* Header row */}
        <div className="grid grid-cols-[22px_1fr_70px_100px_80px_140px] gap-2 px-3 pb-2 pt-3 border-b border-white/[0.05]">
          <button
            onClick={() => selected.size > 0 ? setSelected(new Set()) : setSelected(new Set(items.map((i) => i.id)))}
            className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
              selected.size > 0 && selected.size === items.length ? 'bg-primary-500 border-primary-500' : selected.size > 0 ? 'bg-primary-500/40 border-primary-500/60' : 'border-white/15 hover:border-white/30'
            }`}
          >
            {selected.size > 0 && <span className="text-white text-[9px]">✓</span>}
          </button>
          <ColHead>Item</ColHead>
          <ColHead>Price</ColHead>
          <ColHead>Platforms</ColHead>
          <ColHead>Status</ColHead>
          <ColHead>Actions</ColHead>
        </div>
        <div className="p-1">
          {isLoading ? (
            <div className="p-6 text-center text-white/30 text-sm">Loading...</div>
          ) : items.length === 0 ? (
            <EmptyState onCreate={() => navigate('/dashboard/new')} />
          ) : (
            items.map((item: any) => (
              <ListingRow
                key={item.id}
                item={item}
                selected={selected.has(item.id)}
                onToggle={() => toggleSelect(item.id)}
                onEdit={() => navigate(`/dashboard/listing/${item.id}`)}
                onCrosslist={() => setCrosslistTarget(item.id)}
                onDelete={() => setDeleteTarget(item.id)}
              />
            ))
          )}
        </div>
        </div>
        </div>
      </div>

      {/* Load more */}
      {data && items.length < data.total && (
        <button
          onClick={() => setLimit((l) => l + 100)}
          disabled={isLoading}
          className="btn-secondary w-full text-[12px]"
        >
          {isLoading ? 'Loading…' : `Load more — showing ${items.length} of ${data.total}`}
        </button>
      )}

      {/* AI strip */}
      <div className="bg-secondary-400/[0.05] border border-secondary-400/[0.1] rounded-lg flex items-center justify-between px-3.5 py-2.5">
        <div className="flex items-center gap-2">
          <Sparkles size={16} className="text-secondary-400" />
          <span className="text-[11.5px] text-white/50">
            <strong className="text-secondary-400 font-medium">AI assistant</strong> — generate titles, descriptions and tags for any listing in seconds
          </span>
        </div>
        <button onClick={() => navigate('/dashboard/ai')} className="bg-primary-500/80 text-white text-[11px] font-medium rounded-md px-3 py-1">Try it free</button>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-2.5 bg-bg-raised border border-white/[0.1] rounded-xl px-4 py-2.5 shadow-2xl z-50">
          <span className="text-[12px] font-medium text-white/60 pr-1 border-r border-white/10 mr-1">{selected.size} selected</span>
          <button
            onClick={() => setBulkDeleteConfirm(true)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-danger/[0.08] border border-danger/20 text-danger text-[11px] font-medium hover:bg-danger/[0.14] transition-colors"
          >
            <Trash2 size={12} /> Delete all
          </button>
          <button onClick={() => setSelected(new Set())} className="w-6 h-6 flex items-center justify-center text-white/30 hover:text-white/60 transition-colors">
            <X size={13} />
          </button>
        </div>
      )}

      {/* Single-item crosslist modal */}
      {crosslistTarget && (
        <CrosslistModal
          connectedPlatforms={connectedPlatforms}
          livePlatforms={targetLivePlatforms}
          loading={singleCrosslist.isPending}
          onConfirm={(platforms) => singleCrosslist.mutate({ listingId: crosslistTarget, platforms })}
          onClose={() => setCrosslistTarget(null)}
          onRemovePlatform={(platform) => removeFromPlatform.mutate({ listingId: crosslistTarget, platform })}
          removingPlatform={removeFromPlatform.isPending}
          onDelistPlatform={(platform) => delistViaExtension.mutate({ listingId: crosslistTarget, platform })}
          delisting={delistViaExtension.isPending}
          extensionDetected={extensionDetected}
        />
      )}

      {/* Single-item delete confirm */}
      {deleteTarget && (
        <ConfirmModal
          title="Delete this listing?"
          body="Removes it from Crosova. Does not delist from any marketplaces."
          confirmLabel="Delete"
          danger
          loading={singleDelete.isPending}
          onConfirm={() => singleDelete.mutate(deleteTarget)}
          onClose={() => setDeleteTarget(null)}
        />
      )}

      {/* Bulk delete confirm + progress */}
      {bulkDeleteConfirm && (
        <ConfirmModal
          title={bulkDelete.isPending ? `Deleting listings…` : `Delete ${selected.size} listing${selected.size > 1 ? 's' : ''}?`}
          body="This cannot be undone. Listings will be removed from Crosova but not delisted from any marketplaces."
          confirmLabel="Delete all"
          danger
          loading={bulkDelete.isPending}
          progress={deleteProgress}
          total={deleteTotal}
          onConfirm={() => bulkDelete.mutate()}
          onClose={() => { if (!bulkDelete.isPending) setBulkDeleteConfirm(false); }}
        />
      )}
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ label, value, change, colour }: { label: string; value: React.ReactNode; change: string; colour: 'primary' | 'secondary' | 'success' | 'danger' }) {
  const accent = { primary: { bar: '#7c5cfc', text: '#a78bfa' }, secondary: { bar: '#2de8c8', text: '#2de8c8' }, success: { bar: '#4ade80', text: '#4ade80' }, danger: { bar: '#f97316', text: '#f97316' } }[colour];
  return (
    <div className="card p-3.5 relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: accent.bar }} />
      <div className="text-[10.5px] text-white/30 mb-1">{label}</div>
      <div className="text-[21px] font-semibold tracking-tight" style={{ color: accent.text }}>{value}</div>
      <div className="text-[10px] mt-0.5" style={{ color: accent.text }}>{change}</div>
    </div>
  );
}

function ColHead({ children }: { children: React.ReactNode }) {
  return <div className="text-[10px] font-semibold uppercase tracking-wide text-white/[0.18]">{children}</div>;
}

function ListingRow({ item, selected, onToggle, onEdit, onCrosslist, onDelete }: {
  item: any; selected: boolean;
  onToggle: () => void; onEdit: () => void; onCrosslist: () => void; onDelete: () => void;
}) {
  const qc = useQueryClient();
  const boost = useMutation({
    mutationFn: (platform: string) => api.post(`/listings/${item.id}/boost`, { platform }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['listings'] }),
  });
  const livePlatforms = (item.platformListings ?? []).filter((pl: any) => pl.status === 'live');

  return (
    <div
      onClick={onEdit}
      className={`grid grid-cols-[22px_1fr_70px_100px_80px_140px] gap-2 px-3 py-2.5 rounded-lg items-center cursor-pointer mb-0.5 border transition-colors ${
        selected ? 'bg-primary-500/[0.07] border-primary-500/[0.12]' : 'border-transparent hover:bg-white/[0.025]'
      }`}
    >
      {/* Checkbox */}
      <div
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        className={`w-3.5 h-3.5 rounded flex-shrink-0 flex items-center justify-center cursor-pointer border transition-colors ${
          selected ? 'bg-primary-500 border-primary-500' : 'border-white/15 hover:border-white/35'
        }`}
      >
        {selected && <span className="text-white text-[9px]">✓</span>}
      </div>

      {/* Photo + title */}
      <div className="flex items-center gap-2.5 min-w-0">
        {item.photos?.[0]?.url
          ? <img src={item.photos[0].url} alt="" className="w-9 h-9 rounded-lg object-cover flex-shrink-0" />
          : <div className="w-9 h-9 rounded-lg bg-white/[0.04] flex-shrink-0" />
        }
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className="text-[12.5px] font-medium text-white/85 truncate">{item.title}</span>
            {item.listingType === 'auction' && (
              <span className="flex-shrink-0 text-[8.5px] px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-400 border border-amber-400/25 font-semibold tracking-wide">AUC</span>
            )}
            {item.listingType === 'fixed' && (
              <span className="flex-shrink-0 text-[8.5px] px-1.5 py-0.5 rounded bg-primary-500/15 text-primary-300 border border-primary-500/25 font-semibold">BIN</span>
            )}
          </div>
          <div className="text-[10px] text-white/25 mt-0.5">{item.brand && `${item.brand} · `}{item.condition?.replace(/_/g, ' ')}</div>
        </div>
      </div>

      {/* Price */}
      <div className="text-[13px] font-medium text-secondary-400">£{(item.price / 100).toFixed(2)}</div>

      {/* Platforms */}
      <div className="flex gap-0.5 flex-wrap">
        {(item.platformListings ?? []).map((pl: any) => (
          <PlatformBadge key={pl.platform} platform={pl.platform} />
        ))}
      </div>

      {/* Status */}
      <div><StatusBadge status={item.status} /></div>

      {/* Actions — always visible, stop row click propagation */}
      <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-1 justify-end">
        {/* Crosslist */}
        <button
          onClick={onCrosslist}
          title="Crosslist to another platform"
          className="flex items-center gap-1 px-2 py-1.5 rounded-md text-[11px] text-white/40 hover:text-secondary-400 hover:bg-secondary-400/[0.08] transition-colors border border-transparent hover:border-secondary-400/20"
        >
          <Repeat size={12} />
          <span>Crosslist</span>
        </button>

        {/* Boost (only for live listings) */}
        {livePlatforms.length > 0 && (
          <button
            onClick={() => boost.mutate(livePlatforms[0].platform)}
            disabled={boost.isPending}
            title={`Boost on ${livePlatforms[0].platform}`}
            className="p-1.5 rounded-md text-white/30 hover:text-secondary-400 hover:bg-secondary-400/[0.08] transition-colors"
          >
            <Zap size={12} className={boost.isPending ? 'animate-pulse' : ''} />
          </button>
        )}

        {/* Delete */}
        <button
          onClick={onDelete}
          title="Delete listing"
          className="p-1.5 rounded-md text-white/30 hover:text-danger hover:bg-danger/[0.08] transition-colors"
        >
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
}

function PlatformBadge({ platform }: { platform: string }) {
  const colour = PLATFORM_COLOURS[platform] ?? '#555';
  return (
    <div className="w-5 h-5 rounded text-[8px] font-bold flex items-center justify-center text-white" style={{ background: colour }}>
      {(PLATFORM_NAMES[platform as Platform] ?? platform).slice(0, 2)}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cls = ({ live: 'badge-live', sold: 'badge-sold', draft: 'badge-draft', failed: 'badge-fail', publishing: 'badge-draft', delisted: 'badge-draft' } as Record<string, string>)[status] ?? 'badge-draft';
  return <span className={cls}>{status[0].toUpperCase() + status.slice(1)}</span>;
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="p-10 text-center">
      <div className="text-white/40 text-sm mb-3">No listings yet</div>
      <button onClick={onCreate} className="btn-primary">Create your first listing</button>
    </div>
  );
}

function CrosslistModal({ connectedPlatforms, livePlatforms, onConfirm, onClose, loading, onRemovePlatform, removingPlatform, onDelistPlatform, delisting, extensionDetected }: {
  connectedPlatforms: string[]; livePlatforms: string[];
  onConfirm: (platforms: string[]) => void; onClose: () => void; loading: boolean;
  onRemovePlatform: (platform: string) => void; removingPlatform: boolean;
  onDelistPlatform: (platform: string) => void; delisting: boolean; extensionDetected: boolean;
}) {
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const liveSet = new Set(livePlatforms);

  function toggle(p: string) {
    const next = new Set(chosen);
    next.has(p) ? next.delete(p) : next.add(p);
    setChosen(next);
  }

  const willUpdate = [...chosen].filter((p) => liveSet.has(p));
  const willCreate = [...chosen].filter((p) => !liveSet.has(p));

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-bg-surface border border-white/[0.08] rounded-2xl w-full max-w-sm p-5">
        <h2 className="text-sm font-semibold mb-1">Crosslist listing</h2>
        <p className="text-[11px] text-white/30 mb-4">Choose where to publish</p>
        <div className="space-y-1.5 mb-3">
          {connectedPlatforms.map((p) => {
            const on = chosen.has(p);
            const isLive = liveSet.has(p);
            const canRemove = isLive && isExtensionPlatform(p);
            return (
              <div
                key={p}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border transition-colors ${on ? 'border-secondary-400/30 bg-secondary-400/[0.06]' : 'border-white/[0.07] bg-white/[0.02]'}`}
              >
                <button onClick={() => toggle(p)} className="flex items-center gap-2.5 flex-1 text-left min-w-0">
                  <div className="w-5 h-5 rounded text-[8px] font-bold flex items-center justify-center text-white flex-shrink-0" style={{ background: PLATFORM_COLOURS[p] ?? '#555' }}>
                    {(PLATFORM_NAMES[p as Platform] ?? p).slice(0, 2)}
                  </div>
                  <span className={`text-[12px] flex-1 ${on ? 'text-secondary-400' : 'text-white/60'}`}>{PLATFORM_NAMES[p as Platform] ?? p}</span>
                  {isLive && <span className="text-[9.5px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/20">live</span>}
                  <div className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 ${on ? 'bg-secondary-400' : 'border border-white/15'}`}>
                    {on && <Check size={10} className="text-bg-base" strokeWidth={3} />}
                  </div>
                </button>
                {canRemove && extensionDetected && (
                  <button
                    onClick={() => onDelistPlatform(p)}
                    disabled={delisting}
                    title={`Delete this listing on ${PLATFORM_NAMES[p as Platform] ?? p}`}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-400 border border-red-500/20 hover:bg-red-500/25 flex-shrink-0 whitespace-nowrap disabled:opacity-50"
                  >
                    {delisting ? 'Delisting…' : 'Delist'}
                  </button>
                )}
                {canRemove && (
                  <button
                    onClick={() => onRemovePlatform(p)}
                    disabled={removingPlatform}
                    title={`Mark as no longer on ${PLATFORM_NAMES[p as Platform] ?? p} (records only)`}
                    className="text-[10px] text-orange-400 hover:underline flex-shrink-0 whitespace-nowrap"
                  >
                    Not listed?
                  </button>
                )}
              </div>
            );
          })}
          {connectedPlatforms.length === 0 && <p className="text-[11px] text-white/30 text-center py-3">No platforms connected — go to Settings first</p>}
        </div>
        {livePlatforms.some((p) => isExtensionPlatform(p)) && (
          <p className="text-[10px] text-white/30 mb-3 leading-relaxed">
            <span className="text-red-400">Delist</span> deletes the listing on Vinted/Depop for you (opens it in your browser).
            Use <span className="text-orange-400">Not listed?</span> only if you've already removed it there and just need Crosova's records updated.
          </p>
        )}
        {chosen.size > 0 && (willUpdate.length > 0 || willCreate.length > 0) && (
          <div className="rounded-lg bg-white/[0.03] border border-white/[0.06] px-3 py-2.5 mb-3 space-y-1">
            {willUpdate.length > 0 && <p className="text-[11px] text-amber-300"><strong>Update existing:</strong> {willUpdate.map((p) => PLATFORM_NAMES[p as Platform] ?? p).join(', ')}</p>}
            {willCreate.length > 0 && <p className="text-[11px] text-secondary-400"><strong>Publish new:</strong> {willCreate.map((p) => PLATFORM_NAMES[p as Platform] ?? p).join(', ')}</p>}
          </div>
        )}
        <div className="flex gap-2">
          <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          <button onClick={() => onConfirm(Array.from(chosen))} disabled={chosen.size === 0 || loading} className="btn-primary flex-1">
            {loading ? 'Queueing...' : chosen.size === 0 ? 'Select a platform' : `Confirm (${chosen.size})`}
          </button>
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ title, body, confirmLabel, danger, onConfirm, onClose, loading, progress = 0, total = 0 }: {
  title: string; body: string; confirmLabel: string; danger?: boolean;
  onConfirm: () => void; onClose: () => void; loading: boolean;
  progress?: number; total?: number;
}) {
  const showProgress = loading && total > 0;
  const pct = total > 0 ? Math.round((progress / total) * 100) : 0;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-bg-surface border border-white/[0.08] rounded-2xl w-full max-w-sm p-5">
        <h2 className="text-sm font-semibold mb-1">{title}</h2>
        <p className="text-[11px] text-white/40 mb-4">{body}</p>

        {showProgress && (
          <div className="mb-4">
            <div className="flex justify-between text-[10px] text-white/40 mb-1.5">
              <span>{progress} of {total} deleted</span>
              <span>{pct}%</span>
            </div>
            <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
              <div
                className="h-full bg-danger/70 rounded-full transition-all duration-300"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )}

        <div className="flex gap-2">
          <button onClick={onClose} disabled={loading} className="btn-secondary flex-1 disabled:opacity-40">Cancel</button>
          <button onClick={onConfirm} disabled={loading} className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-60 ${danger ? 'bg-danger/80 hover:bg-danger text-white' : 'btn-primary'}`}>
            {loading ? (total > 0 ? `Deleting ${progress}/${total}…` : 'Working…') : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
